"use client";

import { Suspense, useCallback, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";
import { DirectionProvider } from "@radix-ui/react-direction";
import { store } from "@/store/index";
import { ConfirmProvider } from "@/context/Confirm";
import { ImagePreviewProvider } from "@/context/ImagePreview";
import { SplitScreenProvider } from "@/components/layout/split-screen/split-screen-context";
import { MaintainanceModeFallback } from "@/utils/lazyLoadComponents";
import { preloadByUrl } from "@/utils/preloadByUrl";
import { fetchMaintenanceStatus } from "@/api/maintenance.api";
import { ScheduledMaintenanceBanner } from "@/components/common/ScheduledMaintenanceBanner";
import type { IMaintenanceCheckResult } from "@/types/maintenance.interface";
import { MAINTENANCE_CHANGED_EVENT } from "@/utils/maintenance-events";
import { Spinner } from "@/components/ui/v2/spinner";
import App from "@/App";

const NULL_STATUS: IMaintenanceCheckResult = {
  isActive: false,
  maintenanceMessage: null,
  isScheduled: false,
  scheduledStartAt: null,
  scheduledEndAt: null,
  scheduledMessage: null,
};

export function MaintenanceAwareRoot() {
  const { i18n } = useTranslation();
  const [status, setStatus] = useState<IMaintenanceCheckResult | null>(null);

  const refresh = useCallback(async () => {
    const s = await fetchMaintenanceStatus().catch(() => NULL_STATUS);
    setStatus(s);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    window.addEventListener(MAINTENANCE_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(MAINTENANCE_CHANGED_EVENT, refresh);
  }, [refresh]);

  useEffect(() => {
    if (status?.isActive) return;
    const warm = () =>
      [
        "/",
        "/company",
        "/company/employees",
        "/company/time-attendance",
        "/time-attendance",
        "/company/leave-management",
        "/leave-management",
      ].forEach(preloadByUrl);
    const ric = (
      window as Window & {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => void;
      }
    ).requestIdleCallback;
    if (typeof ric === "function") ric(warm, { timeout: 4000 });
    else setTimeout(warm, 1500);
  }, [status?.isActive]);

  // Spinner, not a blank page — the maintenance probe can take up to its 4s cap
  // when the API is down.
  if (status === null)
    return <Spinner wrapperClassName="h-screen" className="size-10" />;

  return (
    // Radix primitives (Tabs, Select, Accordion, RadioGroup, …) fall back to
    // `dir="ltr"` on their own root when not told otherwise — this makes that
    // the app's actual active direction everywhere, instead of it needing to
    // be threaded into every individual usage.
    <DirectionProvider dir={i18n.dir()}>
      <Provider store={store}>
        {status.isActive ? (
          <Suspense fallback={null}>
            <MaintainanceModeFallback message={status.maintenanceMessage} />
          </Suspense>
        ) : (
          <SplitScreenProvider>
            <ConfirmProvider>
              <ImagePreviewProvider>
                {status.isScheduled &&
                  status.scheduledStartAt &&
                  status.scheduledEndAt && (
                    <ScheduledMaintenanceBanner
                      scheduledStartAt={status.scheduledStartAt}
                      scheduledEndAt={status.scheduledEndAt}
                      message={status.scheduledMessage}
                    />
                  )}
                <App />
              </ImagePreviewProvider>
            </ConfirmProvider>
          </SplitScreenProvider>
        )}
        <Toaster position="top-center" />
        <Tooltip
          id="hrm-tooltip"
          place="top"
          offset={8}
          style={{
            backgroundColor: "var(--color-foreground)",
            backgroundImage:
              "linear-gradient(135deg, var(--color-foreground) 0%, var(--color-foreground) 100%)",
            color: "var(--color-surface)",
            fontSize: "12px",
            lineHeight: 1.45,
            fontWeight: 500,
            letterSpacing: "0.01em",
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid rgba(100, 56, 255, 0.35)",
            boxShadow:
              "0 12px 28px -12px rgba(100, 56, 255, 0.45), 0 6px 14px -6px rgba(16, 24, 40, 0.25)",
            maxWidth: "min(280px, calc(100vw - 32px))",
            whiteSpace: "normal",
            wordBreak: "break-word",
            zIndex: 9999,
          }}
        />
      </Provider>
    </DirectionProvider>
  );
}
